import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { SearchPageFilterInput } from '../../__generated__/api/types';
import {
  FILTERS_PARAM,
  encodeFiltersToQueryParam,
  getFiltersInputFromQueryParams,
} from '../libs/getFiltersFromParams';

/**
 * Single source of truth for the search page state, readable and writable from
 * anywhere in the render tree.
 */
export const SEARCH_PATHNAME = '/soeg';

export type SearchIntent = 'default' | 'suggestion' | 'history';

export type SearchState = {
  pathname: string;
  search: string;
  query: string;
  filters: SearchPageFilterInput[];
  /** How `intentQuery` was triggered. */
  intent: SearchIntent;
  /** The query `intent` applies to. Any other query has intent `'default'`. */
  intentQuery: string;
};

const SERVER_STATE: SearchState = {
  pathname: SEARCH_PATHNAME,
  search: '',
  query: '',
  filters: [],
  intent: 'default',
  intentQuery: '',
};

const QUERY_PATH_PATTERN = new RegExp(`^${SEARCH_PATHNAME}/(.+)$`);

function getQueryFromPath(pathname: string): string {
  const match = pathname.match(QUERY_PATH_PATTERN);
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // Hand-edited URL with a malformed escape; show it verbatim rather than throw.
    return match[1];
  }
}

function normalizeSearch(search?: string): string {
  if (!search) return '';
  return search.startsWith('?') ? search : `?${search}`;
}

function readLocationState(intent: SearchIntent, intentQuery: string): SearchState {
  if (typeof window === 'undefined') return SERVER_STATE;

  const { pathname, search } = window.location;
  return {
    pathname,
    search,
    query: getQueryFromPath(pathname),
    filters: getFiltersInputFromQueryParams({
      searchParams: new URLSearchParams(search),
    }),
    intent,
    intentQuery,
  };
}

let state: SearchState = readLocationState('default', '');

const listeners = new Set<() => void>();

function emit() {
  listeners.forEach(listener => listener());
}

/**
 * Re-read the location-derived slice while preserving the current transient
 * state, then notify subscribers.
 *
 * No-op when the URL is unchanged, which makes this safe to call
 * unconditionally: `useSearchLocationSync` fires after our own writes too, and
 * simply finds nothing to do.
 */
function syncFromLocation({ force = false }: { force?: boolean } = {}) {
  if (typeof window === 'undefined') return;

  // Only our own writes force a sync, so an unforced one means the router
  // moved. If it is landing a navigation we asked for, the URL it restores is
  // the one it was handed — which can be several keystrokes stale, because the
  // `replaceState` writes since then were invisible to it. The store knows what
  // the user actually typed, so it re-asserts rather than adopting.
  if (!force && awaitingRouteNavigation) {
    awaitingRouteNavigation = false;

    if (getQueryFromPath(window.location.pathname) !== state.query) {
      writeLocation({ query: state.query });
      return;
    }
  }

  const { pathname, search } = window.location;
  if (!force && pathname === state.pathname && search === state.search) return;

  state = readLocationState(state.intent, state.intentQuery);
  emit();
}

/**
 * Set while a route navigation this store asked for is in flight. See
 * `syncFromLocation`, which consumes it when the navigation lands.
 */
let awaitingRouteNavigation = false;

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): SearchState {
  return state;
}

function getServerSnapshot(): SearchState {
  return SERVER_STATE;
}

// --- Location syncing --------------------------------------------------------

/**
 * Keeps the store in step with router-driven navigation (`<Link>`,
 * `router.push`, back/forward). Mount exactly once, as high in the tree as the
 * store is consumed.
 *
 * Our own writes do not go through the router, so `pathname`/`searchParams`
 * will not change for them — those sync themselves inside `writeLocation`, and
 * the effect below no-ops.
 */
export function useSearchLocationSync() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams?.toString() ?? '';

  // biome-ignore lint/correctness/useExhaustiveDependencies: the router values are the trigger, not inputs to the effect body
  useEffect(() => {
    syncFromLocation();
  }, [pathname, search]);
}

// --- Writers -----------------------------------------------------------------

type LocationUpdate = {
  /** Omit to keep the current query. */
  query?: string;
  /** Omit to keep the current filters; pass `[]` to clear them. */
  filters?: SearchPageFilterInput[];
};

/**
 * The single place that writes search state to the URL. Uses `replaceState`
 * deliberately: refining a search must not push a history entry per keystroke,
 * so "back" from a search leaves the search page rather than stepping through
 * previous queries.
 */
function writeLocation({ query, filters }: LocationUpdate) {
  const pathname =
    query === undefined
      ? window.location.pathname
      : query
        ? `${SEARCH_PATHNAME}/${encodeURIComponent(query)}`
        : SEARCH_PATHNAME;

  const params = new URLSearchParams(window.location.search);
  if (filters !== undefined) {
    const encoded = encodeFiltersToQueryParam(filters);
    if (encoded) {
      params.set(FILTERS_PARAM, encoded);
    } else {
      params.delete(FILTERS_PARAM);
    }
  }

  const nextUrl = `${pathname}${normalizeSearch(params.toString())}${window.location.hash}`;

  window.history.replaceState(window.history.state, '', nextUrl);

  syncFromLocation({ force: true });
}

/**
 * Whether moving to `nextQuery` crosses between `/soeg` and `/soeg/<query>`.
 *
 * Must be read *before* the URL is rewritten, which is why it is private and
 * only ever called from `setQuery`: afterwards the location already describes
 * the destination, so it can only ever answer `false`.
 */
function crossesSearchRoute(nextQuery: string): boolean {
  const hasQueryNow = getQueryFromPath(window.location.pathname) !== '';
  return hasQueryNow !== Boolean(nextQuery);
}

/**
 * Update the active query, optionally replacing the filters, and record how the
 * query was triggered. The intent is bound to `query` and is reported only for
 * that query.
 *
 * Returns whether this moved between `/soeg` and `/soeg/<query>`. Those are two
 * separate routes, so a `true` means the caller must follow up with a router
 * navigation — `replaceState` alone would change the address bar while the
 * previously matched route stayed mounted. It is returned rather than offered
 * as a separate check so it cannot be read too late.
 */
export function setQuery(
  query?: string,
  options?: { filters?: SearchPageFilterInput[]; intent?: SearchIntent }
): boolean {
  if (typeof window === 'undefined') return false;

  const next = query ?? '';
  const intent = options?.intent ?? 'default';
  const crossesRoute = crossesSearchRoute(next);

  if (crossesRoute) awaitingRouteNavigation = true;

  state = { ...state, intent, intentQuery: next };
  writeLocation({ query: next, filters: options?.filters });

  return crossesRoute;
}

/**
 * Persist the selected filters to the URL (`?filters=`).
 *
 * Changing filters clears any recorded intent: refining by filter is by
 * definition a refine, no longer a suggestion/history pick.
 */
export function setFilters(filters: SearchPageFilterInput[]) {
  if (typeof window === 'undefined') return;

  state = { ...state, intent: 'default', intentQuery: '' };
  writeLocation({ filters });
}

// --- Selector hooks ----------------------------------------------------------

/**
 * Subscribe to a derived slice of the search state.
 */
function useSearchSelector<T>(
  selector: (state: SearchState) => T,
  isEqual: (a: T, b: T) => boolean = Object.is
): T {
  const cache = useRef<{
    selector: (state: SearchState) => T;
    state: SearchState;
    value: T;
  } | null>(null);

  const select = (current: SearchState): T => {
    const previous = cache.current;
    const reusable = previous !== null && previous.selector === selector;

    if (reusable && previous.state === current) {
      return previous.value;
    }

    const next = selector(current);

    if (reusable && isEqual(previous.value, next)) {
      // Keep the previous reference so consumers do not re-render.
      cache.current = { selector, state: current, value: previous.value };
      return previous.value;
    }

    cache.current = { selector, state: current, value: next };
    return next;
  };

  return useSyncExternalStore(
    subscribe,
    () => select(getSnapshot()),
    () => select(getServerSnapshot())
  );
}

function filtersEqual(a: SearchPageFilterInput[], b: SearchPageFilterInput[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((filter, index) => {
    const other = b[index];
    return (
      filter.id === other.id &&
      filter.value.length === other.value.length &&
      filter.value.every((value, valueIndex) => value === other.value[valueIndex])
    );
  });
}

const selectQuery = (s: SearchState) => s.query;
const selectFilters = (s: SearchState) => s.filters;
const selectPathname = (s: SearchState) => s.pathname;

export function useSearchQueryInstant(): string {
  return useSearchSelector(selectQuery);
}

export function useSearchFilters(): SearchPageFilterInput[] {
  return useSearchSelector(selectFilters, filtersEqual);
}

export function useSearchPathname(): string {
  return useSearchSelector(selectPathname);
}

/**
 * The intent recorded for `query`, or `'default'` for any other query.
 *
 * Callers pass the term the request is actually being made for (typically the
 * debounced one), so the intent is reported for exactly the request it
 * describes and expires on its own.
 */
export function useSearchIntentFor(query: string): SearchIntent {
  const selector = useCallback(
    (s: SearchState) => (s.intentQuery === query ? s.intent : 'default'),
    [query]
  );
  return useSearchSelector(selector);
}
